import {
  getSession,
  listSessions,
  locateChat,
  type ChatSessionDto,
} from '@/lib/api/sessions.client';

/**
 * The workspace a chat is read in (KAN-1266): which workspace the tab is in,
 * where a chat that does not open here lives, moving the tab there, and
 * telling the user when that happened. Opening a chat (``load-chat.ts``) is
 * the one caller.
 */

/**
 * Switch this tab's workspace to ``tenantId``. Returns whether a switch
 * happened (``false`` when the tab is already there). Throws when the backend
 * refuses the switch (caller is no member of that workspace).
 */
export async function switchTabTenant(tenantId: string): Promise<boolean> {
  if (sessionStorage.getItem('currentTenantId') === tenantId) return false;
  // Dynamic import: the tenant store imports the chat store, so a static
  // import here would close the cycle.
  const { useTenantStore } = await import('@/lib/stores/tenant.store');
  await useTenantStore.getState().switchTenant(tenantId);
  return true;
}

/** The workspace this tab is in now: the tenant store's, else the stored one. */
export async function activeTabTenantId(): Promise<string | null> {
  // Dynamic import for the same cycle reason as in ``switchTabTenant``.
  const { useTenantStore } = await import('@/lib/stores/tenant.store');
  return useTenantStore.getState().currentTenant?.id ?? sessionStorage.getItem('currentTenantId');
}

/**
 * Tell the user the tab just moved to another workspace. Nothing on the chat
 * surface says so otherwise, and the switch changes where the next chat they
 * start lands. Runs outside React, so it builds its own translator; the key is
 * the one the agent page uses for the same switch.
 */
export async function announceWorkspaceSwitch(): Promise<void> {
  try {
    const [{ toast }, { createTranslator }, { resolveInitialLocale }, { loadMessages }] =
      await Promise.all([
        import('sonner'),
        import('use-intl'),
        import('@/lib/i18n/resolve-locale'),
        import('@/lib/i18n/messages'),
      ]);
    const { useTenantStore } = await import('@/lib/stores/tenant.store');
    const name = useTenantStore.getState().currentTenant?.name;
    if (!name) return;
    const locale = resolveInitialLocale();
    const messages = (await loadMessages(locale)) as {
      agents: { layout: Record<string, string> };
    };
    const t = createTranslator({ locale, messages, namespace: 'agents.layout' });
    toast.info(t('workspaceSwitched', { tenant: name }));
  } catch (error) {
    console.warn('Workspace switch could not be announced', error);
  }
}

// The session of a chat: its own endpoint, or, should that fail, the first
// page of the plain list. ``null`` when neither knows it.
export const readSessionRow = async (
  chatId: string,
  limit: number
): Promise<ChatSessionDto | null> => {
  try {
    return await getSession(chatId);
  } catch (error) {
    console.warn('Session-Metadaten (GET) fehlgeschlagen, fallback auf listSessions', error);
  }
  try {
    const { sessions } = await listSessions({ limit, offset: 0 });
    return sessions.find((session) => session.id === chatId) ?? null;
  } catch (listError) {
    console.warn('Session-Metadaten (listSessions) fehlgeschlagen', listError);
    return null;
  }
};

// The caller's OWN chat living in another of their workspaces than the active
// one (a link, a bookmark, a tab left in the other workspace): the workspace
// to move to, else ``null``. A share grantee (``sharedBy``) stays put — a
// grant is valid from their own workspace (KAN-1010).
export const otherOwnWorkspace = (
  session: ChatSessionDto | null,
  myUserId: string | null,
  activeTenantId: string | null
): string | null => {
  if (!session?.tenantId || session.sharedBy || !activeTenantId) return null;
  if (session.userId == null || session.userId !== myUserId) return null;
  return session.tenantId === activeTenantId ? null : session.tenantId;
};

/**
 * Where a chat that did not open in this workspace lives, when it is the
 * caller's own or shared with them (KAN-1754): a bookmark, the history, the
 * notice that someone shared a chat with them, all leading into another of
 * their workspaces. ``null`` otherwise. The backend names a workspace only for
 * those two kinds of chat and answers for every other one, an agent run of a
 * foreign workspace included, as for a chat that does not exist; super-admins
 * get no wider reach. Whether the caller may enter the workspace is still the
 * switch's call.
 */
export async function reachableChatWorkspace(chatId: string): Promise<string | null> {
  try {
    return (await locateChat(chatId)) || null;
  } catch {
    return null;
  }
}
